/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useRef, useState } from "react";

/** Common Components */
import MolGrid, { Column } from "@moltech/molui/molgrid";
import Buttons from "../../../../../../components/ui/button/Buttons";
import CardSection from "../../../../../../components/ui/card/CardSection";
import CustomCenterModel from "../../../../../../components/ui/customCenterModel/CustomCenterModel";
import { AppIcons } from "../../../../../../data/appIcons";
import formatDate from "../../../../../../components/finalMolGrid/libs/formatDate";

/** Utilities */
import ConsumptionDetail from "./features/ContractConsumptionDetail";
import ExpenseDetail from "./features/ExpenseContractDetail";
import { useExportContractRepairsListMutation, useLazyGetContractRepairsListQuery } from "../../../../../../app/services/contractAPI";
import { saveAs } from 'file-saver';
import { ContractContext } from "../../../../../../utils/context/ContractContext";

const Repairs = () => {
  const { contractId } = useContext(ContractContext);
  const gridRef = useRef();
  const [gridData, setGridData] = useState([]);
  const currentDate = formatDate(new Date(), "MM-DD-YYYY");
  const [selectedRow, setSelectedRow] = useState(null);

  const [getAllContractRepairsList, {
    isSuccess: isAllContractRepairsListFetched,
    data: AllContractRepairsListData
  }] = useLazyGetContractRepairsListQuery();

  const [exportContractsRepairList, {
    isLoading: isExporting,
    isSuccess: isAllExported,
    data: exportAllData
  }] = useExportContractRepairsListMutation();

  useEffect(() => {
    getAllContractRepairsList(contractId);
  }, [contractId]);

  useEffect(() => {
    if (isAllContractRepairsListFetched && AllContractRepairsListData) {
      setGridData(AllContractRepairsListData);
    }
  }, [isAllContractRepairsListFetched, AllContractRepairsListData]);

  // Export All Details response handling
  useEffect(() => {
    if (!isExporting && isAllExported && exportAllData) {
      const blob = new Blob([exportAllData]);
      saveAs(blob, `Contracts Repaires${currentDate}.xlsx`);
    }
  }, [isExporting, isAllExported, exportAllData]);

  const handalExportContracts = () => {
    exportContractsRepairList(contractId);
  }

  const headerContent = () => {
    return (
      <div className="d-flex justify-content-end gap-3 export-icon">
        <Buttons
          buttonText="Extract"
          onClick={handalExportContracts}
          buttonTypeClassName="btn blue-btn"
          titleText="Unassign Selected Items"
        />
      </div>
    );
  };

  const [isExpenseDetailOpen, setIsExpenseDetailOpen] = useState(false);

  const handleExpenseDetailsClick = (rowData) => {
    setIsExpenseDetailOpen(true);
    setSelectedRow(rowData);
  };

  const [isConsumptionDetailOpen, setIsConsumptionDetailOpen] = useState(false);

  const handleCloseModel = () => {
    setIsConsumptionDetailOpen(false);
    setIsExpenseDetailOpen(false);
  };

  const handleConsumptionDetailsClick = (rowData) => {
    setIsConsumptionDetailOpen(true);
    setSelectedRow(rowData);
  };

  const getCtxMenuItems = (row) => {
    if (!row) return [];

    return [
      {
        label: "Consumption Details",
        command: () => handleConsumptionDetailsClick(row),
      },
      {
        label: "Expense Details",
        command: () => handleExpenseDetailsClick(row),
      },
    ];
  };

  const totalConsumption = gridData?.reduce(
    (sum, item) => sum + (item.consumptionAmount || 0),
    0
  );

  const totalExpense = gridData?.reduce(
    (sum, item) => sum + (item.expenseAmount || 0),
    0
  );

  return (
    <>
      <div className="row gap-4">
        <div className="scrollable-grid col-12">
          <CardSection
            cardTitle={`Repairs`}
            customHeader={true}
            headerContent={headerContent()}
          >
            <MolGrid
              ref={gridRef}
              value={gridData}
              grouping
              expandableRowGroups={true}
              paginator={false}
              scrollable
              scrollHeight="flex"
              filterDisplay="row"
              sortMode="single"
              showGridlines
              resizableColumns
              ctxMenuItems={getCtxMenuItems}
            >
              <Column field="sClientName1" header="Client" grouping sortable />
              <Column
                field="sDepartmentName"
                header="Department"
                grouping
                sortable
                bodyStyle={{ textAlign: "Center" }}
              />
              <Column
                field="sSerialNumber"
                header="Serial Number"
                grouping
                sortable
                bodyStyle={{ textAlign: "Center" }}
              />
              <Column field="sScopeTypeDesc" header="Model" bodyStyle={{ textAlign: "Center" }} grouping sortable />
              <Column
                field="sWorkOrderNumber"
                header="Work Order"
                grouping
                sortable
                bodyStyle={{ textAlign: "Center" }}
              />
              <Column
                field="dtTranDate"
                header="Invoice Date"
                colType="datetime"
                format="mm/dd/yyyy"
                grouping
                sortable
                bodyStyle={{ textAlign: "Center" }}
              />
              <Column
                field="dtDateIn"
                header="Date In"
                colType="datetime"
                format="mm/dd/yyyy"
                grouping
                sortable
                bodyStyle={{ textAlign: "Center" }}
              />
              <Column
                field="consumptionAmount"
                header="Consumption"
                body={(rowData) => `$${rowData.consumptionAmount?.toFixed(2)}`}
                footer={`$${totalConsumption.toFixed(2)}`}
                grouping
                sortable
                bodyStyle={{ textAlign: "Right" }}
              />
              <Column
                field="expenseAmount"
                header="Expense Amount"
                body={(rowData) => `$${rowData.expenseAmount?.toFixed(2)}`}
                footer={`$${totalExpense.toFixed(2)}`}
                grouping
                sortable
                bodyStyle={{ textAlign: "Right" }}
              />
            </MolGrid>
          </CardSection>
        </div>
        <CustomCenterModel
          isOpen={isConsumptionDetailOpen}
          useCustomBg={true}
          modalTitle={"Consumption Details"}
          contentClass="content-50"
          onClose={handleCloseModel}
          modalTitleIcon={AppIcons.AddIcon}
        >
          <ConsumptionDetail contractId={parseInt(contractId)} rowData={selectedRow} />
        </CustomCenterModel>
        <CustomCenterModel
          isOpen={isExpenseDetailOpen}
          useCustomBg={true}
          modalTitle={"Expense Details"}
          contentClass="content-50"
          onClose={handleCloseModel}
          modalTitleIcon={AppIcons.AddIcon}
        >
          <ExpenseDetail contractId={parseInt(contractId)} rowData={selectedRow} />
        </CustomCenterModel>
      </div>
    </>
  );
};
export default Repairs;
